import { useMemo } from 'react';

import { useResource } from 'pulse/react';
import { AppSettingsEntity } from 'hs-resources/resources/AppSettings';
import { ObjectTypeDefinitions } from 'hs-resources/resources/ObjectTypeDefinition';
import { parse } from 'hub-http/helpers/params';

import { UI_MODE } from 'framework-listing-lib/constants';
import { UIMode } from 'framework-listing-lib/types';

import {
  ListingLibFrameworkAppSettingsMetaData,
  ListingLibOption,
} from 'framework-listing-lib/internal/frameworkAppSettings/types';
import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import {
  useIsGalleryMode,
  useIsListMode,
  useIsFoldersMode,
} from 'framework-listing-lib/internal/hooks/useUIMode';
import { useListingLibData } from 'framework-listing-lib/internal/providers/DataProvider';

import { LISTING_LIB_APP_SETTING_NAMESPACE } from '../constants';
import {
  getHasApplicationId,
  getIsGatedToAll,
  getIsUngatedToAll,
  getNotHasApplicationId,
} from '../utils';

export function useFetchFrameworkAppSettings() {
  'use memo';
  const currentObjectTypeId = useObjectTypeId();
  const {
    data: objectTypes,
    error: objectTypesError,
    loading: objectTypesLoading,
  } = useResource(ObjectTypeDefinitions);
  const {
    data: appSettings,
    error: appSettingsError,
    loading: appSettingsLoading,
  } = useResource(AppSettingsEntity, {
    params: { appSettingName: LISTING_LIB_APP_SETTING_NAMESPACE },
  });

  const appSettingsForObject = useMemo(() => {
    const fullyQualifiedName = objectTypes?.find(
      (objectTypeDefinition) =>
        objectTypeDefinition.objectTypeId === currentObjectTypeId
    )?.fullyQualifiedName;
    const appSetting =
      fullyQualifiedName === undefined
        ? undefined
        : appSettings?.[fullyQualifiedName];

    if (!appSetting) {
      return undefined;
    }

    return {
      [LISTING_LIB_APP_SETTING_NAMESPACE]: appSetting,
    } as Partial<ListingLibFrameworkAppSettingsMetaData>;
  }, [appSettings, currentObjectTypeId, objectTypes]);

  return {
    data: appSettingsForObject,
    error: objectTypesError || appSettingsError,
    loading: objectTypesLoading || appSettingsLoading,
  };
}

export function useAppSettingsHaveLoaded() {
  const { loading } = useFetchFrameworkAppSettings();

  return !loading;
}

/**
 * Read from app settings and only return ui mode options with
 *  - empty gates and scopes OR
 *  - gates array is not empty, scopes array is empty and user has ALL gates OR
 *  - gates array is empty, scopes array is not empty and user has ALL scopes
 */
export function getAppSettingsUIModes({
  isUngatedFor,
  hasScope,
  applicationId,
  appSettings,
}: {
  isUngatedFor: (gate: string) => boolean;
  hasScope: (scope: string) => boolean;
  applicationId: string;
  appSettings?: ListingLibFrameworkAppSettingsMetaData['frameworkListingLib']['metadata'];
}): UIMode[] {
  if (
    !appSettings ||
    !appSettings.uiModes ||
    appSettings.uiModes.value.length === 0
  ) {
    return [UI_MODE.LIST];
  }

  const enabledUIModes = appSettings.uiModes.value
    .filter((uiModeOption) => {
      const isUngatedToAll = getIsUngatedToAll({
        gates: uiModeOption.gates,
        isUngatedFor,
      });

      const isGatedToAll = getIsGatedToAll({
        gates: uiModeOption.gates,
        isUngatedFor,
      });

      let hasAllScopes = true;

      if (uiModeOption.scopes && uiModeOption.scopes.length) {
        hasAllScopes = uiModeOption.scopes.every(hasScope);
      }

      const hasApplicationId = getHasApplicationId({
        currentApplicationId: applicationId,
        applicationIds: uiModeOption.applicationIds,
      });

      const notHasApplicationId = getNotHasApplicationId({
        currentApplicationId: applicationId,
        applicationIds: uiModeOption.applicationIds,
      });

      return (
        isUngatedToAll &&
        isGatedToAll &&
        hasAllScopes &&
        hasApplicationId &&
        notHasApplicationId
      );
    })
    .map((uiModeOption) => uiModeOption.name);

  return [UI_MODE.LIST, ...enabledUIModes];
}
export function useAppSettingsUIModes() {
  'use memo';
  return useListingLibData().uiModes;
}

/**
 * Read from app settings and only return the option when enabled:
 *  - option is present AND
 *  - gates and scopes are empty OR
 *  - gates array is not empty, scopes array is empty and user has ALL gates OR
 *  - gates array is empty, scopes array is not empty and user has ALL scopes
 * @param optionName
 */
export function getHasAppSettingsOption(
  optionName: ListingLibOption['name'],
  context: {
    isUngatedFor: (gate: string) => boolean;
    hasScope: (scope: string) => boolean;
    applicationId: string;
    appSettings?: ListingLibFrameworkAppSettingsMetaData['frameworkListingLib']['metadata'];
  }
) {
  const { isUngatedFor, hasScope, applicationId, appSettings } = context;
  if (!appSettings || !appSettings.options) {
    return undefined;
  }

  const appSettingsOption = appSettings.options.value.find(
    (val) => val.name === optionName
  );

  if (!appSettingsOption) {
    return undefined;
  }

  const isUngatedToAll = getIsUngatedToAll({
    gates: appSettingsOption.gates,
    isUngatedFor,
  });

  const isGatedToAll = getIsGatedToAll({
    gates: appSettingsOption.gates,
    isUngatedFor,
  });
  let hasAllScopes = true;

  if (appSettingsOption.scopes && appSettingsOption.scopes.length) {
    hasAllScopes = appSettingsOption.scopes.every(hasScope);
  }

  const hasApplicationId = getHasApplicationId({
    currentApplicationId: applicationId,
    applicationIds: appSettingsOption.applicationIds,
  });

  const notHasApplicationId = getNotHasApplicationId({
    currentApplicationId: applicationId,
    applicationIds: appSettingsOption.applicationIds,
  });

  if (
    isUngatedToAll &&
    isGatedToAll &&
    hasAllScopes &&
    hasApplicationId &&
    notHasApplicationId
  ) {
    return appSettingsOption;
  }

  return undefined;
}

export function useAppSettingsOption(optionName: ListingLibOption['name']) {
  'use memo';
  const { getHasAppSettingsOption: memoizedGetHasAppSettingsOption } =
    useListingLibData();
  return memoizedGetHasAppSettingsOption(optionName);
}

export function useHasCustomProperties() {
  'use memo';
  const hasCustomPropertiesOption = useAppSettingsOption('hasCustomProperties');

  return Boolean(hasCustomPropertiesOption);
}

export function useHasEditColumns(hasEditColumnsInFoldersMode?: boolean) {
  'use memo';
  const isListMode = useIsListMode();
  const isFoldersMode = useIsFoldersMode();
  const hasEditColumnsOption = useAppSettingsOption('hasEditColumns');

  return (
    (isListMode || (isFoldersMode && hasEditColumnsInFoldersMode)) &&
    Boolean(hasEditColumnsOption)
  );
}

export function useHasExport() {
  'use memo';
  const isListMode = useIsListMode();
  const hasExportOption = useAppSettingsOption('hasExport');

  return isListMode && Boolean(hasExportOption);
}

export function useHasNestedFolders() {
  'use memo';
  const hasNestedFolders = useAppSettingsOption('hasNestedFolders');

  return Boolean(hasNestedFolders);
}

export function useHasInlineEdit() {
  'use memo';
  const hasInlineEdit = useAppSettingsOption('hasInlineEdit');
  /**
   * Inline editing can also be enabled via query string.
   * This is useful in ATs where we don't have access to change FAS on the fly.
   */
  const queryString = parse(window.location.search.substring(1));

  return Boolean(hasInlineEdit) || queryString.hasInlineEdit === 'true';
}

export function useHasCustomizeGallery() {
  'use memo';
  const isGalleryMode = useIsGalleryMode();
  const hasCustomizeGalleryOption = useAppSettingsOption('hasCustomizeGallery');

  return isGalleryMode && Boolean(hasCustomizeGalleryOption);
}

export function useHasBUNavIntegration() {
  'use memo';
  const hasBUNavIntegration = useAppSettingsOption('hasBUNavIntegration');

  return Boolean(hasBUNavIntegration);
}
